import React, { useState } from 'react';
import { Mail, Phone, MapPin, Clock, Send, Loader2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/translations';
import { companyInfo } from '../data/mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const { language } = useLanguage();
  const t = translations[language];
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, {
        ...formData,
        language: language
      });
      
      if (response.data) {
        const successMsg = language === 'en' 
          ? 'Thank you for contacting us! We will respond within 24-48 hours.'
          : 'Grazie per averci contattato! Ti risponderemo entro 24-48 ore.';
        toast.success(successMsg);
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      const errorMsg = language === 'en'
        ? 'Error sending message. Please try again later.'
        : 'Errore nell\'invio del messaggio. Riprova più tardi.';
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section className="page-hero">
        <div className="page-hero-content">
          <h1 className="page-title">{t.contact.title}</h1>
          <p className="page-subtitle">
            {t.contact.subtitle}
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact-section">
        <div className="contact-container">
          {/* Contact Form */}
          <div className="contact-form-wrapper">
            <h2 className="contact-form-title">{t.contact.formTitle}</h2>
            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-group">
                <label htmlFor="name" className="form-label">
                  {t.contact.name} *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder={language === 'en' ? 'Your name' : 'Il tuo nome'}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email" className="form-label">
                    {t.contact.email} *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="email@esempio.it"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone" className="form-label">
                    {t.contact.phone}
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="+39 123 456 7890"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="subject" className="form-label">
                  {t.contact.subject} *
                </label>
                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="form-select"
                >
                  <option value="">{t.contact.selectSubject}</option>
                  <option value="info-general">{t.contact.subjects.general}</option>
                  <option value="iso-9001">{t.contact.subjects.iso9001}</option>
                  <option value="iso-45001">{t.contact.subjects.iso45001}</option>
                  <option value="iso-14001">{t.contact.subjects.iso14001}</option>
                  <option value="hse">{t.contact.subjects.hse}</option>
                  <option value="quote">{t.contact.subjects.quote}</option>
                  <option value="other">{t.contact.subjects.other}</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="message" className="form-label">
                  {t.contact.message} *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="6"
                  className="form-textarea"
                  placeholder={language === 'en' ? 'Write your message here...' : 'Scrivi qui il tuo messaggio...'}
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary btn-large"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={20} className="spinner" />
                    {t.contact.sending}
                  </>
                ) : (
                  <>
                    <Send size={20} />
                    {t.contact.send}
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="contact-info-wrapper">
            <div className="contact-info-card">
              <h3 className="contact-info-title">{t.contact.infoTitle}</h3>
              
              <div className="contact-info-item">
                <div className="contact-info-icon">
                  <Mail size={24} />
                </div>
                <div className="contact-info-content">
                  <h4>{t.contact.emailLabel}</h4>
                  <a href={`mailto:${companyInfo.email}`}>{companyInfo.email}</a>
                </div>
              </div>

              <div className="contact-info-item">
                <div className="contact-info-icon">
                  <Phone size={24} />
                </div>
                <div className="contact-info-content">
                  <h4>{t.contact.phoneOffice}</h4>
                  <a href={`tel:${companyInfo.phoneOffice}`}>{companyInfo.phoneOffice}</a>
                </div>
              </div>

              <div className="contact-info-item">
                <div className="contact-info-icon">
                  <Phone size={24} />
                </div>
                <div className="contact-info-content">
                  <h4>{t.contact.phoneSales}</h4>
                  <a href={`tel:${companyInfo.phoneSales}`}>{companyInfo.phoneSales}</a>
                </div>
              </div>

              <div className="contact-info-item">
                <div className="contact-info-icon">
                  <MapPin size={24} />
                </div>
                <div className="contact-info-content">
                  <h4>{t.contact.addressLabel}</h4>
                  <p>{companyInfo.address}</p>
                </div>
              </div>

              <div className="contact-info-item">
                <div className="contact-info-icon">
                  <Clock size={24} />
                </div>
                <div className="contact-info-content">
                  <h4>{t.contact.hoursLabel}</h4>
                  <p style={{ whiteSpace: 'pre-line' }}>{t.contact.hours}</p>
                </div>
              </div>

              <div className="contact-info-item contact-piva-item">
                <div className="contact-clean-logo-container">
                  <img 
                    src="https://customer-assets-lqy194kg.emergentagent.net/job_iso-compliance-hub-8/artifacts/in3c33x5_1000035409.png" 
                    alt="Mallorie Consulting Group"
                    className="contact-clean-logo"
                  />
                </div>
                <div className="contact-info-content">
                  <h4>Partita IVA</h4>
                  <p className="piva-number">01506460458</p>
                </div>
              </div>
            </div>

            <div className="coverage-info-card">
              <h3 className="contact-info-title">{t.contact.serviceAreasTitle}</h3>
              <p className="coverage-description">
                {t.contact.serviceAreasDesc}
              </p>
              <div className="coverage-list">
                {companyInfo.regions.map((region, idx) => (
                  <div key={idx} className="coverage-item">
                    <MapPin size={18} />
                    <span>{region}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
